import CloseIcon from '@mui/icons-material/Close';
import JxLineEdit from './JxLineEdit';
import './JxSearchEdit.css';
import { RefreshOutlined } from '@mui/icons-material';

const STRIP_PROPS = ['defaultValue', 'onSearch', 'minLength', 'debounceMs', 'onArrowDown'];

/**
 * JxSearchEdit — debounced search input, extends JxLineEdit.
 * Owns its own value state — caller gets notified via onSearch after debounce.
 * onSearch fires only when value.length >= minLength, otherwise fires with empty string.
 *
 * @param {string}   id
 * @param {string}   [defaultValue='']
 * @param {string}   [placeholder='Search…']
 * @param {(value: string) => void} onSearch     - debounced; fired when length >= minLength or on clear
 * @param {number}   [minLength=3]               - minimum chars before onSearch fires with actual value
 * @param {number}   [debounceMs=300]            - debounce delay in ms
 * @param {boolean}  [disabled=false]
 * @param {() => void} [onArrowDown]             - called when ArrowDown is pressed; stripped from DOM props
 */
export default class JxSearchEdit extends JxLineEdit {
    constructor(props) {
        super(props);
        this.state = { value: props.defaultValue ?? '' };
        this._timer = null;
        this.handleClear = this.handleClear.bind(this);
    }

    // ── getter overrides ──

    getValue() {
        return this.state.value;
    }

    getSuffix() {
        if (this.state.value.length === 0) return null;
        return (
            <div style={{ marginRight: '4px' }}>
                <CloseIcon className="jx-search-edit-clear" onClick={this.handleClear} />
                <RefreshOutlined sx={{ ml: '4px' }} className="jx-search-edit-clear" onClick={this.handleRefresh} />
            </div>
        );
    }

    getPropsToOmit() {
        return STRIP_PROPS;
    }

    // ── handler overrides ──

    handleChange(e) {
        const next = e.target.value;
        this.setState({ value: next });
        this._scheduleSearch(next);
    }

    handleKeyDown(e) {
        if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            this._clearSearch();
            return;
        }
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            this.props.onArrowDown?.();
            return;
        }
        // Enter and other keys — delegate to JxLineEdit
        super.handleKeyDown(e);
    }

    handleClear() {
        this._clearSearch();
    }

    handleRefresh = () => {
        const value = this.getValue();
        this._scheduleSearch(value);
    };

    // ── lifecycle ──

    componentWillUnmount() {
        clearTimeout(this._timer);
    }

    // ── private ──

    _scheduleSearch(value) {
        const { debounceMs = 300, minLength = 3, onSearch } = this.props;
        clearTimeout(this._timer);
        this._timer = setTimeout(function fireSearch() {
            onSearch?.(value.length >= minLength ? value : '');
        }, debounceMs);
    }

    _clearSearch() {
        clearTimeout(this._timer);
        this.setState({ value: '' });
        this.props.onSearch?.('');
    }
}
